import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router';
import { graphql } from '@/gql';
import { Card } from '@/components/ui/Card';

const CompleteExactAuthorizationDoc = graphql(/* GraphQL */ `
  mutation CompleteExactAuthorization($code: String!, $state: String!) {
    completeExactAuthorization(code: $code, state: $state) {
      status
      division
      connectedAt
      lastError
    }
  }
`);

/**
 * OAuth callback for Exact Online. Exact redirects here with `code` + `state`
 * (or `error` / `error_description`). On success we land on Configuration.
 */
export function ExactRedirectPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [complete] = useMutation(CompleteExactAuthorizationDoc);

  const oauthError = params.get('error');
  const oauthErrorDescription = params.get('error_description');
  const code = params.get('code');
  const state = params.get('state');
  const paramFailure = Boolean(oauthError) || !code || !state;
  const paramMessage = oauthError
    ? oauthErrorDescription || oauthError
    : !code || !state
      ? 'Missing authorization code or state from Exact Online.'
      : null;

  const [mutationFailure, setMutationFailure] = useState<string | null>(null);

  // The OAuth state is single use in Go. React StrictMode (dev) runs this effect
  // twice and tears the first run down in between, so the guard lives in a ref
  // and the first call is never cancelled: its result alone drives navigation
  // and the error line.
  const startedFor = useRef<string | null>(null);

  useEffect(() => {
    if (paramFailure || !code || !state) {
      return;
    }
    const key = `${code}:${state}`;
    if (startedFor.current === key) {
      return;
    }
    startedFor.current = key;
    void (async () => {
      try {
        await complete({ variables: { code, state } });
        navigate('/configuration?tab=exact', { replace: true });
      } catch (err) {
        setMutationFailure(err instanceof Error ? err.message : 'Authorization failed');
      }
    })();
  }, [paramFailure, code, state, complete, navigate]);

  const failed = paramFailure || mutationFailure !== null;
  const message = paramMessage ?? mutationFailure ?? 'Connecting to Exact Online…';

  return (
    <div className="mx-auto max-w-lg py-10">
      <Card title="Exact Online" subtitle="OAuth callback">
        <p
          id="admin.exact.redirect.message"
          className={
            failed
              ? 'text-sm text-red-600 dark:text-red-400'
              : 'text-sm text-gray-600 dark:text-gray-300'
          }
        >
          {failed ? <span id="admin.exact.redirect.error">{message}</span> : message}
        </p>
        {failed && (
          <button
            type="button"
            className="mt-4 text-sm font-medium text-brand-500 hover:underline"
            onClick={() => navigate('/configuration?tab=exact')}
          >
            Back to configuration
          </button>
        )}
      </Card>
    </div>
  );
}
